import React, { useState, useEffect, useContext } from 'react';
import Box from '@mui/material/Box';
import '@sencha/best-react-grid/dist/themes/grui.css';
import { useGQLQuery } from '../../../../hooks/useGQLQuery';
import AgGridComponent from '../../../../components/agGridTableComponent';
import Loader from '../../../../components/Loader';
import coaQueries from '../../../../queries/coa';
import Pagination from '../../../../components/paginationTable';
import { ColumnDefs } from '../../../../types/AgGridTypes';
import COADifferent from './COADifferent.style';
import { DEFAULT_PAGE_SIZE } from '../../../../constants';
import { UserContext } from '../../../../store/context/userContext';
import { ProjectContext } from '../../../../store/context/projectContext';
import { OutletContext, QueryErrorResponse } from '../../../../types/OrganisationDetailType';
import { useOutletContext } from 'react-router-dom';
import TotalValueType from '../../../../types/TotalValueType';
import CoaColumn from './CoaColumn';
import { JSONParse } from '../../../../helper/StorageHelper';
import { truthyCodeValidation } from '../../../../helper/validations';
import projectPhase from '../../../../queries/phase';

interface DefaultCoaRenderer {
    labour_hoursTotal: number;
    labourTotal: number;
    distributableTotal: number,
    c_equipmentTotal: number,
    materialTotal: number,
    sustaining_total: number,
    equipmentTotal: number,
    subcontractTotal: number,
    indirectTotal: number,
    total_costTotal: number,
    name: string,
    total_cost: number,
    expansion_cost_total: number;
    initial_cost_total: number;
    modular_buildings_total: number
    total_values: number,
    total_labour_hours: number,
    total_labour_rate: number,
    total_const_distribution: number,
    total_sub_total: number,
    total_const_equip: number,
    total_total: number,
    total_prod_factor: number
}
type PhaseObject = {
    description: string;
    total_cost: number;
    [key: string]: string | number;
  };
  interface DataObject {
    code:string,
    name: string,
    date:string,
    updated_by: string,
    target: {
        value: string,
        checked: boolean
    }
    id: string,
    isCurrentVersion: boolean,
    length: number,
    code1: string,
    code2: string,
    code3: string,
    code4: string,
    code5: string,
    code6: string,
}

interface DataObjectExtended extends DataObject {
    [key: string]: string | number | boolean | {
        value: string,
        checked: boolean
    };
}

interface Props {
    view?: string,
    searchText: string,
    setSearchText?: React.Dispatch<React.SetStateAction<string>>;
    hideUnusedFlag: boolean;
    setIsFetching: React.Dispatch<React.SetStateAction<boolean>>;
}
interface CoaColumnDefs extends ColumnDefs {
    sort?: string,
    pinned?: string,
    level?: string,
}

function CoaPhaseViews(props: Props) {
    const {
        view, searchText, setSearchText, hideUnusedFlag, setIsFetching,
    } = props;
    const classes = COADifferent();
    const [pageSkipValue, setPageSkipValue] = useState(0);
    const [newColumnDefs, setNewColumnDefs] = useState<ColumnDefs[]>([]);
    const [colHeader, setColHeader] = useState<string[]>(['total_cost']);
    const userCtx = useContext(UserContext);
    const projectCtx = useContext(ProjectContext);
    const cont: OutletContext = useOutletContext();

    const displayedCurrencyId = projectCtx?.project?.currency_id;
    const displayCurr = projectCtx?.projectCurrencyData?.getprojectCurrency?.find((curr) => curr.id === displayedCurrencyId);
    const exchangeRate = displayCurr?.exchange_rate || 1;

    const handleApiError = ({ response }: QueryErrorResponse) => {
        const message = response && response.errors && response.errors[0] ? response.errors[0].message : 'API failed';
        cont.showNotificationBar(message, 'error');
    };

    // Function for Total bottom row
    const createPhaseData = (params: DefaultCoaRenderer[], data: number) => {
        const result: PhaseObject = {
            description: 'TOTAL',
            total_cost: data && +((data / exchangeRate).toFixed(0)),
        };
        for (let i = 0; i < params.length; i += 1) {
            result[`column_${params[i].name}`] = params[i].total_cost && (+(params[i].total_cost) / exchangeRate).toFixed(0);
        }
        return [result];
    };

    // Fetch new page data
    const handlePageChange = (page: number) => {
        const skipValue = (page - 1) * DEFAULT_PAGE_SIZE;
        setPageSkipValue(skipValue);
    };

    // Fetch API data
    const { data, isFetching, refetch } = useGQLQuery(
        `GetCOAPhase-${projectCtx?.project?.id || 0}`,
        coaQueries.GET_COA_PHASE(
            pageSkipValue,
            DEFAULT_PAGE_SIZE,
            '',
            '',
            userCtx?.user?.default_org_id || '',
            projectCtx?.project?.id || '',
            projectCtx?.project?.version_id || '',
            hideUnusedFlag,
        ),
        {},
        { onError: handleApiError },
    );

    const { data: projectPhse, refetch: phaseRefetch, isFetching: isPhaseFetching } = useGQLQuery(
        'GetProjectPhase',
        projectPhase.GET_PROJECT_PHASE(
            userCtx?.user?.default_org_id || '',
            projectCtx?.project?.id || '',
            projectCtx?.project?.version_id || '',
        ),
        {},
        {
            onError: handleApiError,
        },
        '/setting',
    );
    // For COA Phase...
    const phaseViewData = data?.coaphase;
    const tablePhaseData = phaseViewData?.data;
    const tablePhaseViewData: DataObject[] = tablePhaseData ? JSONParse<DataObject[]>(tablePhaseData?.toString()) : [];
    const phasePageInfo = phaseViewData?.pageInfo;
    const phaseTotalValue: DefaultCoaRenderer[] = phaseViewData ? JSONParse<DefaultCoaRenderer[]>(phaseViewData.total_values?.toString()) : [];

    // For Bottom Total Value...
    let phasePinnedBottomRowData;
    if (phaseTotalValue && phaseTotalValue.length > 0) {
        const totalCostValue: number = phaseTotalValue.reduce((accumulator, currentItem) => accumulator + (+currentItem.total_cost), 0);
        phasePinnedBottomRowData = createPhaseData(phaseTotalValue, totalCostValue);
    }

    useEffect(() => {
        refetch();
    }, [pageSkipValue, hideUnusedFlag]);

    const columnDefs: CoaColumnDefs[] = [
        {
            field: 'code',
            headerName: 'COA',
            cellRenderer: CoaColumn,
            sort: 'asc',
            initialWidth: 120,
            type: 'string',
            initialHide: false,
        },
        {
            field: 'description',
            headerName: 'Account Description',
            cellRenderer: CoaColumn,
            initialWidth: 400,
            type: 'string',
            initialHide: false,
            enableValue: true,
        },
        {
            field: 'total_cost',
            headerName: 'Total',
            cellRenderer: CoaColumn,
            pinned: 'right',
            initialWidth: 150,
            maxWidth: 400,
            type: 'numericColumn',
            initialHide: false,
            enableValue: true,
        },
    ];

    useEffect(() => {
        setNewColumnDefs(columnDefs);
    }, [view]);

    const phaseTotalPages = (phasePageInfo && phasePageInfo.totalcount) ? Math.ceil(phasePageInfo.totalcount / DEFAULT_PAGE_SIZE) : 0;

    // Add dynamic column for phase view
    const phaseDynamicCol = () => {
        if (projectPhse && projectPhse?.phase && view === 'Phase View') {
            setColHeader(['total_cost']);
            const tempArr: string[] = [];
            const colArr: CoaColumnDefs[] = [];
            projectPhse?.phase.forEach((item) => {
                colArr.push(
                    {
                        field: `column_${item.name}`,
                        headerName: item.name,
                        cellRenderer: CoaColumn,
                        pinned: '',
                        initialWidth: 150,
                        maxWidth: 500,
                        type: 'numericColumn',
                        initialHide: false,
                        enableValue: true,
                        level: item.code,
                    },
                );
            });
            colArr.sort((a, b) => {
                if (a.level && b.level) {
                    if (a.level < b.level) return -1;
                    if (a.level > b.level) return 1;
                }
                return 0;
            });
            colArr.forEach((col) => {
                columnDefs.push(col);
                tempArr.push(`${col?.field}`);
                setNewColumnDefs(columnDefs);
            });
            setColHeader([...colHeader, ...tempArr]);
        }
    };

    const generateColumnDefs = () => {
        ['code1', 'code2', 'code3', 'code4', 'code5', 'code6'].forEach((code, index) => {
            const flag = (tablePhaseViewData?.map((item) => ((item as DataObjectExtended)[code])))?.some(truthyCodeValidation);
            if (flag) {
                columnDefs.push({
                    field: code,
                    headerName: `Code${index + 1}`,
                    initialWidth: 120,
                    cellRenderer: CoaColumn,
                    type: 'string',
                    initialHide: flag,
                    enableValue: false,
                });
            }
        });
        setNewColumnDefs(columnDefs);
    };

    useEffect(() => {
        phaseDynamicCol();
        generateColumnDefs();
    }, [view, isFetching]);

    useEffect(() => {
        setIsFetching(isFetching);
    }, [isFetching]);

    return (
        <div>
            {/* Page Header Start */}
            <Loader loading={isFetching} />
            <Box className={isFetching ? classes.opacityBlur : ''}>
                <AgGridComponent
                    columnDefs={newColumnDefs}
                    rowData={tablePhaseViewData}
                    changeSortingValue={() => {}}
                    pinnedBottomRowData={phasePinnedBottomRowData}
                    isClipboardEnabled
                    isExportEnabled
                    isRangeSelectable
                    isToolPanelsEnabled
                    isStatusBarEnabled
                    isGroupable
                    CoaRowBgStyle
                    moduleName="coa_phaseview"
                    colFormat={colHeader}
                    quickFilterText={searchText}
                    setSearchText={setSearchText}
                />
            </Box>
            {/* <Box>
                <Pagination
                    totalPages={phaseTotalPages}
                    handlePageChange={handlePageChange}
                />
            </Box> */}
        </div>
    );
}

export default CoaPhaseViews;
